import React, { useEffect, useState } from "react";
import { api } from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import PartySearch from "../pages/components/PartySearch";
import PartySummary from "../pages/components/PartySummary";
import { Party, PartySummaryDto } from "../pages/types/types";
import "../css/cards.css";
import "../css/forms.css";
import "./UserPartyLink.css";

type PersonLink = { partyId?: number; displayName?: string; recordStatus?: string; summary?: PartySummaryDto };

export default function UserPartyLink({ userId, showPersonalDetails = true }: { userId: string; showPersonalDetails?: boolean }) {
  const { selectedTenant } = useAuth();
  const [link, setLink] = useState<PersonLink | null>(null);
  const [person, setPerson] = useState<Party | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const tenantId = selectedTenant?.tenantId;

  useEffect(() => {
    let active = true;
    setLink(null);
    setPerson(null);
    setFailed(false);
    api<PersonLink>(`/api/platformuser/${encodeURIComponent(userId)}/party-link`)
      .then((result) => { if (active) setLink(result); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [userId, tenantId, reload]);

  const save = async (createPerson: boolean) => {
    setBusy(true);
    try {
      const saved = await api<PersonLink>(`/api/platformuser/${encodeURIComponent(userId)}/party-link`, {
        method: "POST",
        body: { partyId: createPerson ? null : person?.partyId, createPerson },
        notifySuccess: createPerson ? "Person created and linked to the login." : "Person linked to the login.",
      });
      setLink(saved);
      setPerson(null);
    } catch { /* The API client shows the standard error notification. */ }
    finally { setBusy(false); }
  };

  if (link?.partyId) return <div className="user-person-link">
    <PartySummary partyId={link.partyId} cards showPersonalDetails={showPersonalDetails} summary={link.summary ?? { partyId: link.partyId, displayName: link.displayName ?? `Person ${link.partyId}`, partyType: "Person", contactMethods: [] }} />
    {link.recordStatus !== "ACTIVE" && <p className="field-hint">This party is inactive.</p>}
  </div>;

  return <div className="user-person-link"><section className="card" aria-label="Personal details">
    <div className="card-header"><div className="card-title">{showPersonalDetails ? "Personal Details" : "Party Record"}</div></div>
    {failed ? <p>Unable to load the linked person. <button type="button" className="text-button" onClick={() => setReload((value) => value + 1)}>Try again</button></p>
      : !link ? <p>Loading linked person…</p>
      : <>
        <p>This login has no linked person. Search for the existing person first to avoid creating a duplicate.</p>
        <PartySearch key={`${userId}-${tenantId}`} searchLabel="Find an existing person" onPartySelected={setPerson} />
        {person && person.partyType !== "Person" && <p role="alert">Select a person rather than an organisation or family.</p>}
        <div className="user-person-link-actions">
          <button type="button" className="secondary-button" disabled={busy || !person || person.partyType !== "Person"} onClick={() => void save(false)}>Link selected person</button>
          <button type="button" className="secondary-button" disabled={busy} onClick={() => void save(true)}>Create person from login</button>
        </div>
        <p className="field-hint">Creation uses the login’s saved name and email. Service membership and supplier setup are completed separately.</p>
      </>}
  </section>
    {person?.partyType === "Person" && <PartySummary key={person.partyId} partyId={person.partyId} cards showPersonalDetails={showPersonalDetails} />}
  </div>;
}
